// src/services/accountingClosureService.js

import { API_BASE_URL } from "./apiConfig";
import { logApiContext } from "../config/apiBaseUrl";

const API_URL = `${API_BASE_URL}/api/turnos`; // ruta unificada de turnos
const logRequest = (action, url) =>
  logApiContext(`accountingClosureService:${action}`, { url, API_URL });
const logInfo = (label, data) => {
  try {
    console.info(label, data);
  } catch (e) {
    /* noop */
  }
};

/**
 * Obtiene los cierres contables de varios drivers.
 * @param {string[]} driverIds – Array de IDs de drivers.
 * @param {string} [bossId] – ID del jefe que solicita los cierres (se envía como query para trazabilidad en el backend).
 * @returns {Promise<object[]>} – Promise que resuelve con el array de cierres.
 */
export const getClosuresByDrivers = async (driverIds, bossId) => {
  try {
    if (!Array.isArray(driverIds) || driverIds.length === 0) {
      throw new Error("Debes pasar un array de driverIds");
    }

    const url = new URL(`${API_URL}/by-drivers`);
    if (bossId) url.searchParams.set("bossId", bossId);

    logRequest("getClosuresByDrivers", url.toString());

    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`, // si usas auth por token
      },
      body: JSON.stringify({ driverIds }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Error al obtener cierres por drivers"
      );
    }

    const { closures } = await response.json();
    return closures;
  } catch (error) {
    console.error("Error al obtener cierres por drivers:", {
      message: error?.message,
      code: error?.code,
      stack: error?.stack,
    });
    throw error;
  }
};

export const getMyClosures = async ({ driverId, userId } = {}) => {
  const storedUser = (() => {
    try {
      const saved = localStorage.getItem("userData");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  })();

  const effectiveDriverId = driverId || storedUser.driverId;
  const effectiveUserId = userId || storedUser.userId;

  const url = new URL(API_URL);
  if (effectiveDriverId) url.searchParams.set("driverId", effectiveDriverId);
  if (effectiveUserId) url.searchParams.set("userId", effectiveUserId);

  logRequest("getMyClosures", url.toString());

  const res = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    console.error("Error getMyClosures response", {
      status: res.status,
      statusText: res.statusText,
      body: text,
    });
    throw new Error(text || "Error al obtener cierres");
  }
  return res.json();
};

export const getClosureDetail = async (id) => {
  const url = `${API_URL}/${id}`;
  logRequest("getClosureDetail", url);
  logInfo("[WEB][getClosureDetail] GET", url);
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "No se pudo cargar el detalle del turno");
  }
  return res.json();
};

export const deleteClosureById = async (id) => {
  const url = `${API_URL}/${id}`;
  logRequest("deleteClosureById", url);
  logInfo("[WEB][deleteClosureById] DELETE", url);
  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "No se pudo eliminar el turno");
  }
  return res.json();
};

export const markClosuresAsLiquidated = async (closureIds = []) => {
  const url = `${API_URL}/mark-liquidated`;
  logRequest("markClosuresAsLiquidated", url);
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ closureIds }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Error al marcar cierres como liquidados");
  }
  return res.json();
};

export const crearLiquidacionDesdeTurnos = async (turnoIds = []) => {
  const url = `${API_BASE_URL}/api/liquidaciones`;
  logRequest("crearLiquidacionDesdeTurnos", url);
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ turnoIds }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Error al crear la liquidación");
  }
  return res.json();
};

export const getAnalyticsSummary = async ({ startDate, endDate, interval } = {}) => {
  const url = new URL(`${API_URL}/analytics`);
  if (startDate) url.searchParams.set("startDate", startDate);
  if (endDate) url.searchParams.set("endDate", endDate);
  if (interval) url.searchParams.set("interval", interval);

  logRequest("getAnalyticsSummary", url.toString());

  const res = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Error al obtener analíticas");
  }
  return res.json();
};
